import { gsap } from "gsap";
import { Text } from "pixi.js";

import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { ScatterStates } from "../../../../abstracts/types/enums";
import { playSoundOnce } from "../../../../abstracts/utils/SoundUtils";
import { DDDSoundNames, DDDWildTTF } from "../../../assetsLoader/DDDAssetsNames";
import { symbolConfig } from "../../../config/DDDConfig";
import { DDDSymbolNames } from "../../../types/DDDEnums";
import { DDDSymbol } from "./DDDSymbol";
import { symbolsResizeConfig } from "./DDDSymbolsResizeConfig";

export class DDDScatterSymbol extends DDDSymbol {
    protected currentState: ScatterStates;
    protected multiplierText: Text;
    protected multiplier: { value: number } = { value: 0 };

    constructor() {
        super(DDDSymbolNames.SCATTER);

        this.currentState = ScatterStates.NORMAL;
    }

    protected generateSymbol(): void {
        const spineName: string = `spine/DDD/${this.name}.json`;

        this.mainSpine = new AbstractSpine(spineName);
        this.mainSpine.getSpineTween(`Normal`);

        this.mainSpine.x =
            symbolConfig.symbolWidth / 2 +
            symbolsResizeConfig.get(this.name as DDDSymbolNames)!.default.offsetX!;
        this.mainSpine.y =
            symbolConfig.symbolHeight / 2 +
            symbolsResizeConfig.get(this.name as DDDSymbolNames)!.default.offsetY!;
        this.addChild(this.mainSpine);

        this.multiplierText = new Text(`X${1}`, {
            fontFamily: DDDWildTTF.FAMILY,
            fontSize: `38px`,
            dropShadow: true,
            dropShadowAlpha: 0.8,
            dropShadowBlur: 0.8,
            dropShadowColor: 0x000000,
            dropShadowDistance: 1,
            fill: `#ffffff`,
            stroke: `#000000`,
            strokeThickness: 1,
            lineJoin: `round`,
        });

        this.multiplierText.name = `multiplierText`;

        this.mainSpine.setSkinByName(`fs`);
    }

    public override getLandingAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return this.mainSpine.getSpineTween(`landing`, false, null, null, () => {
            this.mainSpine.getSpineTween(`Normal`);
        });
    }

    public override getWinAnimation(
        scatterState: ScatterStates = ScatterStates.NORMAL,
        useSfsSkin: boolean = false,
        hasSpecialAnimation: boolean = false
    ): gsap.core.Tween | gsap.core.Timeline {
        this.currentState = scatterState;

        if (hasSpecialAnimation) {
            this.mainSpine.reset();

            if (useSfsSkin) {
                this.mainSpine.setSkinByName(`sfs`);
            }
            return this.mainSpine.getSpineTween(`scatter_activation`, false, () => {});
        }

        switch (scatterState) {
            case ScatterStates.WIN: {
                if (this.isWild) {
                    return this.mainSpine.getSpineTween(`win_transform`, false, () => {});
                }

                if (useSfsSkin) {
                    this.mainSpine.setSkinByName(`sfs`);
                }

                const tl = gsap.timeline({
                    autoRemoveChildren: true,
                    smoothChildTiming: true,
                });

                const animation = this.mainSpine.getSpineTween(`Win`);
                tl.add(animation);

                return tl;
            }

            case ScatterStates.LOSE: {
                let animationName = `wild_transform`;

                return this.mainSpine.getSpineTween(animationName, false, () => {
                    playSoundOnce({
                        name: DDDSoundNames.SCATTER_TURN_TO_WILDS,
                        playOnceMinDuration: 1,
                    });
                });
            }

            case ScatterStates.ANTICIPATION: {
                this.mainSpine.reset();

                let animationName = `scatter_activation`;

                this.mainSpine.getSpineTween(animationName, true);

                break;
            }

            default:
                this.mainSpine.state.setAnimation(0, `Normal`, false);

                break;
        }

        return gsap.timeline();
    }

    public getMultiplierAnimation(wildsCounter: number): gsap.core.Tween | gsap.core.Timeline {
        if (wildsCounter === 1) {
            return gsap.timeline();
        }

        this.multiplierText.text = `X${Math.round(wildsCounter)}`;
        this.multiplier.value = wildsCounter;
        this.multiplierText.scale.set(1.2);
        this.multiplierText.alpha = 0;
        this.multiplierText.x = 43;
        this.multiplierText.y = 100;

        const animation = gsap.to(this.multiplierText, {
            alpha: 1,
            duration: 0.15,
            ease: `power1.in`,
            pixi: { scale: 1, y: 113 },
            onStart: () => {
                this.addChild(this.multiplierText);
            },
        });

        return animation;
    }

    public getUpgradeAnimation(): gsap.core.Tween | gsap.core.Timeline {
        this.multiplierText.text = `+2`;
        this.multiplierText.scale.set(1.2);
        this.multiplierText.alpha = 0;
        this.multiplierText.x = 43;
        this.multiplierText.y = 100;

        const animation = gsap.to(this.multiplierText, {
            alpha: 1,
            duration: 0.15,
            ease: `power1.in`,
            pixi: { scale: 1, y: 113 },
            onStart: () => {
                this.addChild(this.multiplierText);
            },
        });

        return animation;
    }

    override reset() {
        super.reset();
        this.currentState = ScatterStates.NORMAL;
        this.isWild = false;
        this.mainSpine.setSkinByName(`fs`);

        this.multiplier.value = 0;
        this.removeChild(this.multiplierText);
    }

    protected override resetMainSpine(isSpin: boolean = false) {
        super.resetMainSpine(isSpin);
    }

    resetOnStartSpin() {
        gsap.killTweensOf(this.mainSpine);
        this.alpha = 1;

        this.isWild = false;
        this.currentState = ScatterStates.NORMAL;
    }
}
